import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'danger' | 'outline' | 'link';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-semibold',
      'whitespace-nowrap',
      'flex-shrink-0',
      'rounded-xl',
      'transition-all',
      'duration-150',
      'ease-out',
      'cursor-pointer',
      'select-none',
      'outline-none',
      'border',
      'border-transparent',
      'focus:outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-primary/40',
      'focus-visible:ring-offset-1',
      'disabled:cursor-not-allowed',
      'disabled:opacity-50',
      'active:scale-[0.98]',
    ];

    const variantClasses = {
      primary: [
        'bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600',
        'text-white',
        'shadow-sm shadow-indigo-500/30',
        'hover:shadow-md hover:shadow-indigo-500/40',
        'hover:brightness-105',
      ],
      secondary: [
        'bg-secondary/90',
        'text-secondary-foreground',
        'border-border/80',
        'hover:bg-secondary',
        'hover:border-primary/40',
        'shadow-2xs',
      ],
      outline: [
        'bg-transparent',
        'text-foreground',
        'border-border',
        'hover:bg-secondary/70',
        'hover:border-primary/40',
        'shadow-2xs',
      ],
      ghost: [
        'bg-transparent',
        'text-foreground/90',
        'hover:bg-secondary/80',
        'hover:text-primary',
      ],
      destructive: [
        'bg-gradient-to-r from-red-500 to-rose-600',
        'text-white',
        'shadow-sm shadow-red-500/30',
        'hover:shadow-md hover:shadow-red-500/40',
        'hover:brightness-105',
      ],
      danger: [
        'bg-gradient-to-r from-red-500 to-rose-600',
        'text-white',
        'shadow-sm shadow-red-500/30',
        'hover:shadow-md hover:shadow-red-500/40',
        'hover:brightness-105',
      ],
      link: [
        'bg-transparent',
        'text-primary',
        'hover:underline',
        'underline-offset-4',
        'p-0',
        'h-auto',
        'min-w-0',
        'rounded-none',
      ],
    };

    const sizeClasses = {
      xs: ['px-3', 'py-1', 'text-xs', 'h-7', 'min-w-[48px]', 'gap-1'],
      sm: ['px-4', 'py-1.5', 'text-xs', 'h-8', 'min-w-[56px]', 'gap-1.5'],
      md: ['px-6', 'py-2', 'text-sm', 'h-10', 'min-w-[64px]', 'gap-2'], // M3 Specification: 40px height, 24px (px-6) padding
      lg: ['px-7', 'py-2.5', 'text-base', 'h-12', 'min-w-[80px]', 'gap-2.5'],
    };

    const iconSizeClasses = {
      xs: 'w-3 h-3',
      sm: 'w-3.5 h-3.5', // Linear icons are often smaller
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    };

    const classes = clsx(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth && 'w-full',
      className
    );

    const iconElement = icon && (
      <span className={clsx('flex-shrink-0', iconSizeClasses[size])}>
        {icon}
      </span>
    );

    const loadingSpinner = (
      <svg
        className={clsx('animate-spin flex-shrink-0', iconSizeClasses[size])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {loading && loadingSpinner}
        {!loading && icon && iconPosition === 'left' && iconElement}
        {children}
        {!loading && icon && iconPosition === 'right' && iconElement}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };